import { createContext, useContext, useCallback, useState } from "react";
import { useAuth } from "./AuthContext";
import { useVisibilityPolling } from "../hooks/useVisibilityPolling";
import { smsAPI, imAPI, callsAPI, voicemailAPI, tasksAPI } from "../services/api";

/**
 * CountsProvider — ONE app-wide poller for the notification/unread badges that
 * were previously fetched independently by the Sidebar (and other comms
 * components), each on its own fast interval. Centralizing them here:
 *   - de-duplicates the same counts being fetched by multiple components,
 *   - pauses automatically when the tab is hidden (useVisibilityPolling),
 *   - uses saner intervals than the old ~8-15s per-component timers.
 * Only polls while a user is logged in.
 */
const CountsContext = createContext(null);

// Any component can read the shared counts; returns {} before the provider
// mounts so consumers never crash.
export const useCounts = () => useContext(CountsContext) || {};

const COUNTS_INTERVAL_MS = 20000; // SMS / IM / missed-calls / voicemail badges
const TASKS_INTERVAL_MS = 60000;  // task counts change slowly

export function CountsProvider({ children }) {
  const { user } = useAuth();
  const enabled = !!user;

  const [unreadCounts, setUnreadCounts] = useState({});
  const [totalUnread, setTotalUnread] = useState(0);
  const [imUnread, setImUnread] = useState(0);
  const [imMentions, setImMentions] = useState(0);
  const [missedCalls, setMissedCalls] = useState(0);
  const [vmCount, setVmCount] = useState(0);
  const [taskCounts, setTaskCounts] = useState({ open: 0, overdue: 0 });

  const fetchCounts = useCallback(async () => {
    try {
      const res = await smsAPI.getUnreadCounts();
      const raw = res.data?.counts || {};
      const counts = {};
      let total = 0;
      Object.entries(raw).forEach(([companyName, n]) => {
        const name = (companyName || "").toLowerCase();
        const key = name.includes("institute")
          ? "institute"
          : name.includes("avida")
          ? "avida"
          : "bylyngo";
        counts[key] = (counts[key] || 0) + n;
        total += n;
      });
      setUnreadCounts(counts);
      setTotalUnread(total);
    } catch (e) { /* silent */ }

    try { const r = await imAPI.getUnreadCount(); setImUnread(r.data?.count || 0); } catch (e) { /* ignore */ }
    try { const r = await imAPI.getUnreadMentions(); setImMentions(r.data?.count || 0); } catch (e) { /* ignore */ }
    try { const r = await callsAPI.getMissedCount(); setMissedCalls(r.data?.count || 0); } catch (e) { /* ignore */ }
    try { const r = await voicemailAPI.getCount(); setVmCount(r.data?.unread || r.data?.count || 0); } catch (e) { /* ignore */ }
  }, []);

  const fetchTasks = useCallback(async () => {
    try { const r = await tasksAPI.counts(); setTaskCounts(r.data || { open: 0, overdue: 0 }); } catch (e) { /* ignore */ }
  }, []);

  useVisibilityPolling(fetchCounts, COUNTS_INTERVAL_MS, { enabled });
  useVisibilityPolling(fetchTasks, TASKS_INTERVAL_MS, { enabled });

  return (
    <CountsContext.Provider
      value={{ unreadCounts, totalUnread, imUnread, imMentions, missedCalls, vmCount, taskCounts, refresh: fetchCounts }}
    >
      {children}
    </CountsContext.Provider>
  );
}
